import useAppContext from '@/src/hooks/useAppContext';
import { createMetricSender } from '@/src/metrics/send-metric';
import { DH_IMAGES, DTO, URL_VENDOR } from '@/src/utils/constants';
import { redirectToDeeplink } from '@/src/utils/deeplink';
import { getVendorRankType } from '@/src/utils/rankType/getVendorRankType';
import { shopImpressions as shopImpressionsLoaded } from '@/src/utils/tracking/home/shopImpressionsLoaded';
import { shopImpressions as trackingImpressions } from '@/src/utils/tracking/home/viewImpressionsLoaded';
import {
  DEFAULT_ORIGIN_RESTAURANTS,
  restaurants as trackingRestaurants,
} from '@/src/utils/tracking/restaurants/restaurants';
import ProductCardWrapper from '@components/ProductCardWrapper/ProductCardWrapper';
import SkeletonFoodDesktop from '@components/Skeleton/skeleton-food-desktop/SkeletonFoodDesktop';
import SkeletonFood from '@components/Skeleton/skeleton-food/SkeletonFood';
import { Space } from '@components/Space/Space';
import Swiper from '@components/Swiper';
import { VendorItemCard } from '@components/VendorItem/VendorItem';
import { VendorItemDesktop } from '@components/VendorItemDesktop/VendorItemDesktop';
import useGetFood from '@hooks/useGetFood';
import { format } from '@pedidosya/currency-formatter';
import LastCard from '@pedidosya/food-home-web-components/atoms/LastCard';
import ProductCardMVertical from '@pedidosya/web-fenix/patterns/ProductCardMVertical';
import ProductSwimlaneMVertical from '@pedidosya/web-fenix/patterns/ProductSwimlaneMVertical';
import VendorRow from '@pedidosya/web-fenix/patterns/VendorRow';
import Highlight from '@pedidosya/web-fenix/system/Highlight';
import { useRouter } from 'next/router';
import { createElement, Fragment, useCallback, useEffect } from 'react';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import { IRestaurants } from './types';

const Container = styled.div<{ isDesktop; activeTab }>`
  ${({ activeTab }) =>
    !activeTab &&
    `
    display:none;
  `}
  padding: 16px 0 40px 0;
  flex: 1;
  ${({ isDesktop }) =>
    isDesktop &&
    `
    padding-top: 16px !important;
    padding-left: 0 !important;
  `}
`;

const LastCardContainer = styled.div`
  display: flex;
  min-width: 195px;
`;

const LastCardSwiper = styled(LastCard)<{ isDesktop }>`
  min-height: 100% !important;
  width: 195px;
  ${({ isDesktop }) =>
    isDesktop &&
    `
  width: 168px !important;
  cursor: pointer;
  &:hover {
    background-color: rgba(16, 4, 35, 4%);
    border: 1px solid #100423;
  }
  `}
`;

const StyledProductCardWrapper = styled.div`
  will-change: transform;
  flex-shrink: 0;

  img {
      will-change: transform;
      object-fit: contain;
      width: 100%;
      height: auto;
  }

   > div img { 
      will-change: transform;
      object-fit: contain;
      width: 100%;
      height: auto;
   } 

  div:has(> picture:has(> img)) {
    flex-shrink: 0;
  }
  
  div:has(> img) {
    flex-shrink: 0;
  }
`;

export const Restaurants = ({
  location,
  pagination,
  origin,
  isDesktop,
  platform,
  isRevamp,
}: IRestaurants) => {
  const router = useRouter();
  const { activeTab } = useAppContext();
  const metricSender = createMetricSender();

  const {
    loading,
    vendorError,
    vendors,
    setLoading,
    page,
    setPage,
    totalVendors,
    size,
    midasRequestId,
    vendorsPerPage,
  } = useGetFood(pagination);

  // const vendors = keysToCamel(vendorItemModel.vendors);

  const onClickVendor = async (vendor) => {
    if (
      platform === 'web' ||
      isDesktop ||
      platform === 'unknown' ||
      platform === 'mweb'
    ) {
      router.push(vendor?.url);
    } else {
      redirectToDeeplink(vendor?.redirectLink);
    }
  };

  useEffect(() => {
    if (vendorsPerPage.length > 0 && page > 1) {
      trackingRestaurants({
        type: 'promotions_landing.expanded',
        payload: {
          origin: origin || DEFAULT_ORIGIN_RESTAURANTS,
          contentsIds: vendorsPerPage,
          page,
          businessTypes: ['RESTAURANT'],
          type: 'partner',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (vendors?.length > 0 && page === 1) {
      const uniqueTokens = {};
      const vendorIds = [];
      const positionsGoldVipVendors = [];
      let vendorsQuantityGoldVip = 0;

      vendors?.forEach((vendor, index) => {
        if (vendor?.id) {
          vendorIds.push(vendor.id);
        }
        if (vendor?.ncrToken) {
          if (!uniqueTokens[vendor.ncrToken]) {
            uniqueTokens[vendor.ncrToken] = true;
            positionsGoldVipVendors.push(index);
            vendorsQuantityGoldVip += 1;
          }
        }
      });
      const ncrTokensVendors = Object.keys(uniqueTokens).join(', ');
      trackingImpressions({
        type: 'shop_list.updated',
        payload: {
          contentIds: vendorIds?.join(','),
          ncrRequestToken: ncrTokensVendors,
          shopGoldVip: vendorsQuantityGoldVip,
          shopGoldVipPositions: positionsGoldVipVendors,
          shopListBusinessIndex: 'restaurant',
          shopListBusinessType: 'restaurant',
          shopQuantityTotal: vendors?.length,
          screenType: 'promotions-section-restaurants',
          screenName: 'PromotionsSectionViewController',
        },
      });
    }
  }, [vendors, page]);

  const renderSkeleton = () => {
    if (loading && !vendorError) {
      if (isDesktop) {
        return (
          <SkeletonFoodDesktop data-testid="skeleton-food-desktop" count={9} />
        );
      }
      return (
        <SkeletonFood
          data-testid="skeleton-food"
          count={9}
          isRevamp={isRevamp}
        />
      );
    }
    return null;
  };

  const trackingShopImpressionsLoad = (vendor, position) =>
    shopImpressionsLoaded({
      type: 'shop_impressions.loaded',
      payload: {
        occasion: vendor?.occasion,
        shopId: vendor?.id,
        businessType: 'restaurant',
        position,
        rankType: vendor?.rankType,
        isNew: vendor?.isNew,
        ncrRequestToken: vendor?.ncrToken,
        productSku: null,
        shopDeliveryFee: vendor?.delivery?.deliveryFeeWithDiscount,
        shopDeliveryTime: vendor?.delivery?.deliveryTime,
        rating: vendor?.rating,
        isVendorSwimlane: false,
        screenName: 'promotions-section-restaurants',
        screenType: 'promotions-section-restaurants',
      },
    });

  const renderVendors = (init, end) =>
    vendors.slice(init, end)?.map((vendor, position) => (
      <Fragment key={vendor?.id}>
        {isDesktop ? (
          <VendorItemDesktop
            vendorName={vendor?.name}
            variant="header-off"
            isNew={vendor?.isNew}
            rating={vendor?.rating}
            occasion={vendor?.occasion}
            isMarkedAsFavorite={false}
            deliveryETAVariant={vendor?.deliveryEtaVariant}
            deliveryFeeVariant={vendor?.deliveryFeeVariant}
            labels={vendor?.labels}
            images={{
              logo: `${URL_VENDOR}${vendor?.logo}`,
            }}
            delivery={vendor?.delivery}
            status={vendor?.status}
            renderPlusTag={vendor?.renderPlusTag}
            renderFavToggle={false}
            onToggleFavorite={() => ''}
            onClick={() => onClickVendor(vendor)}
            hideDivider
            countryId={location?.country?.id}
          />
        ) : (
          <VendorItemCard
            variant="header-off"
            vendorName={vendor?.name}
            rating={vendor?.rating}
            labels={vendor?.labels}
            renderFavToggle={false}
            images={{
              logo: `${URL_VENDOR}${vendor?.logo}`,
            }}
            delivery={vendor?.delivery}
            deliveryETAVariant={vendor?.deliveryEtaVariant}
            deliveryFeeVariant={vendor?.deliveryFeeVariant}
            status={vendor?.status}
            isNew={vendor?.isNew}
            renderPlusTag={vendor?.labels?.isLoyalty}
            ocassion={vendor?.occasion}
            redirectLink={vendor?.redirectLink}
            paymentMethods={vendor?.paymentMethods}
            vendor={vendor}
            location={location}
            onClick={() => onClickVendor(vendor)}
          />
        )}

        <Space mt={8} />
        <Swiper
          overflow="visible"
          spaceBetween={8}
          id={`swiper-card-${position}`}
          flex="0 0 90%"
          left="center"
          section="food"
          quantityChildren={vendor?.productsDecorated?.length}
          paddingLeft={isDesktop ? '0' : '16px'}
        >
          {vendor?.productsDecorated?.map(
            ({ redirectLink, subsidizedItem: item }, index) => (
              <InView
                as="div"
                threshold={0.5}
                onChange={(inView) => {
                  if (inView && activeTab === 'restaurants') {
                    trackingShopImpressionsLoad(vendor, position);
                    trackingRestaurants({
                      type: 'swimlane.shown',
                      payload: {
                        screenType: 'promotions-section-restaurants',
                        swimlanePosition: position,
                        swimlaneContent: item?.id,
                        shopId: vendor?.id,
                        position: index,
                        swimlaneType: 'product',
                      },
                    });
                  }
                }}
                triggerOnce
                key={item?.id}
              >
                <ProductCardWrapper
                  key={item?.id}
                  index={index}
                  item={item}
                  vendor={vendor}
                  isDesktop={isDesktop}
                  location={location}
                  platform={platform}
                  redirectLink={redirectLink}
                  midasRequestId={midasRequestId}
                  hiddenCTA
                  fallbackSRC={`${URL_VENDOR}null`}
                  className="nested-dish"
                />
              </InView>
            ),
          )}
          <LastCardContainer>
            <LastCardSwiper
              isDesktop={isDesktop}
              description="Ir al menú"
              onClick={() => onClickVendor(vendor)}
            />
          </LastCardContainer>
        </Swiper>
        <Space mb={16} />
      </Fragment>
    ));

  const renderPrice = (item, isFinalPrice = false) => {
    const price = isFinalPrice
      ? item?.price?.finalPrice
      : item?.price?.originalPrice;
    return `${format(price, location?.country?.shortName)?.price}`;
  };

  const COUNTRY_DISCOUNT_TAGS = {
    1: 'OFF',
    2: 'DCTO.',
    3: 'OFF',
    7: 'DSCTO.',
    13: 'DSCTO.',
    15: 'OFF',
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tagDiscount = () => {
    const tag = COUNTRY_DISCOUNT_TAGS[location?.country?.id];
    return tag || DTO;
  };

  const renderTagDiscount = useCallback(
    (item) => {
      if (item?.price?.discounts?.length > 0) {
        if (item?.price?.discounts[0]?.amount > 0) {
          return `${item?.price?.discounts[0]?.amount}% ${tagDiscount()}`;
        }
      }
      return null;
    },
    [tagDiscount],
  );

  const getTagData = useCallback(
    (item) => {
      const isPlus = item?.price?.discounts?.[0]?.isLoyalty;
      return {
        label: renderTagDiscount(item),
        category: isPlus ? 'peya-plus' : 'deals-and-discounts',
        hasPlus: isPlus,
      };
    },
    [renderTagDiscount],
  );

  const formatPrice = (price) => format(
    Number(
      price.replace('$', '')
    ),
    location?.country?.shortName
  )?.price;

  const renderProductSwimlaneMVertical = () =>
    vendors?.map((vendor, position) => (
      <div style={{ paddingBottom: 20 }} key={vendor?.id}>
        <VendorRow
          variant="header-off"
          title={vendor?.name}
          rate={vendor?.rating}
          labels={vendor?.labels}
          renderFavToggle={false}
          logo={`${URL_VENDOR}${vendor?.logo}?width=100}`}
          delivery={vendor?.delivery}
          deliveryETAVariant={vendor?.deliveryEtaVariant}
          deliveryFeeVariant={vendor?.deliveryFeeVariant}
          ocassion={vendor?.occasion}
          renderPlusTag={vendor?.labels?.isLoyalty}
          onClick={() => onClickVendor(vendor)}
          rightIntern={vendor?.isNew ? 'tag-s' : 'rating'}
          rightInternLabel={vendor?.isNew ? 'Nuevo' : null}
          status={vendor?.status?.showOverlay ? 'closed' : 'open'}
          stateText={vendor?.status?.title}
        >
          {(vendor?.labels?.discountLabel || vendor?.labels?.markupLabel) &&
            createElement(VendorRow.TagGroup, {
              view: 'stacked',
              tags: [
                vendor?.labels?.discountLabel?.trim() && {
                  id: '1',
                  label: vendor?.labels?.discountLabel?.replace(
                    /HASTA/,
                    (match) => match.charAt(0) + match.slice(1).toLowerCase(),
                  ),
                  category: 'deals-and-discounts',
                  hasPlus: vendor?.labels?.isLoyalty,
                },
                vendor?.labels?.markupLabel?.trim() && {
                  id: '2',
                  label: vendor?.labels?.markupLabel,
                  category: 'positive',
                },
              ].filter(Boolean),
            })}
          {createElement(VendorRow.ServiceInfo, {
            showInfoTime: true,
            deliveryFeePrice: formatPrice(vendor?.delivery?.deliveryFeeWithDiscount),
            deliveryFeePriceOriginal: formatPrice(vendor?.delivery?.deliveryFee),
            deliveryTime: vendor?.delivery?.deliveryTime,
            pickupDistance: vendor?.delivery?.distance,
            pickupTime: vendor?.delivery?.pickupTime,
            kind: vendor?.occasion?.toLowerCase(),
            deliveryFeeVariant: vendor?.deliveryFeeVariant,
            showLabels: false,
            view: 'inline',
          })}
          {createElement(
            VendorRow.HighLightGroupBottom,
            {},
            [
              vendor?.labels?.sponsoringLabel &&
                createElement(Highlight, {
                  label: vendor.labels.sponsoringLabel,
                }),
              vendor?.labels?.conditionLabels?.[0] &&
                createElement(Highlight, {
                  label: vendor.labels.conditionLabels[0],
                }),
            ].filter(Boolean),
          )}
        </VendorRow>
        <div
          style={{
            display: 'flex',
            overflow: 'auto',
            scrollbarWidth: 'none',
            alignItems: 'center',
          }}
        >
          <ProductSwimlaneMVertical>
            <ProductSwimlaneMVertical.Elements>
              {vendor?.productsDecorated?.map(
                ({ redirectLink, subsidizedItem: item }, index) => (
                  <InView
                    as="div"
                    threshold={0.5}
                    onChange={(inView) => {
                      if (inView && activeTab === 'restaurants') {
                        trackingShopImpressionsLoad(vendor, position);
                        trackingRestaurants({
                          type: 'swimlane.shown',
                          payload: {
                            screenType: 'promotions-section-restaurants',
                            swimlanePosition: position,
                            swimlaneContent: item?.id,
                            shopId: vendor?.id,
                            position: index,
                            swimlaneType: 'product',
                          },
                        });
                      }
                    }}
                    triggerOnce
                    key={item?.id}
                  >
                    <StyledProductCardWrapper>
                      <ProductCardMVertical
                        imageMode="fill"
                        imageAspectRadio="4/3"
                        onContent={false}
                        tags={renderTagDiscount(item) && [getTagData(item)]}
                        productBody={{
                          layout: 'title-first',
                          title: item?.name,
                          price: renderPrice(item, true),
                          priceOriginal: renderPrice(item),
                        }}
                        imageSrc={`${DH_IMAGES}/${item?.images[0]?.url}`}
                        onClickVendor={() => {
                          onClickVendor(vendor);
                        }}
                        onClickCard={() => {
                          handleOnClickedCard(
                            item,
                            vendor,
                            position,
                            redirectLink,
                          );
                          redirectToDeeplink(item?.redirectLink);
                        }}
                      />
                    </StyledProductCardWrapper>
                  </InView>
                ),
              )}
            </ProductSwimlaneMVertical.Elements>
            <ProductSwimlaneMVertical.Action
              onClick={() => onClickVendor(vendor)}
              label="Ver más"
            />
          </ProductSwimlaneMVertical>
        </div>
      </div>
    ));

  const handleOnClickedCard = async (item, vendor, position, redirectLink) => {
    trackingRestaurants({
      type: 'product.clicked',
      payload: {
        product: item,
        vendor,
        origin: origin || DEFAULT_ORIGIN_RESTAURANTS,
        position,
        listedInfo: 'subsidized',
      },
    });

    trackingRestaurants({
      type: 'shop.clicked',
      payload: {
        shopId: vendor?.id,
        filterValue: location?.searchedLocation?.filterValue,
        screenType: DEFAULT_ORIGIN_RESTAURANTS,
        shopGoldVip: !(
          vendor?.labels?.sponsoringLabel === null ||
          vendor?.labels?.sponsoringLabel === ''
        ),
        ncrRequestToken: vendor?.ncrToken,
        shopSponsoring: getVendorRankType(vendor?.rankType),
        midasRequestId,
        shopSponsoringPlacement: 'shop_list:swimlane',
        currencyCode: location?.country?.currencyIsoCode,
        shopRatingQuality: vendor?.rating,
        shopName: vendor?.name,
        shopDeliveryFee: vendor?.delivery?.deliveryFee,
        shopDeliveryTime: vendor?.delivery?.deliveryTime,
        position: vendor?.rank,
        shopListBusinessType: 'Restaurants',
        shopDistance: vendor?.delivery?.distance,
        shopClickOrigin: 'swimlane',
        clickLocation: 'product_swimlane',
        discount: vendor?.labels?.discountLabel,
        productsDecorated: vendor?.productsDecorated,
        screenName: 'promotions-section-restaurants',
      },
    });

    const redirectToWeb = async (url) => {
      document.location = url;
    };

    if (
      platform === 'web' ||
      isDesktop ||
      platform === 'unknown' ||
      platform === 'mweb'
    ) {
      return redirectToWeb(`${vendor?.url}?p=${item?.id}`);
    }
    await metricSender.onDishRestaurantClicked();
    return redirectToDeeplink(redirectLink);
  };

  return (
    <Container isDesktop={isDesktop} activeTab={activeTab === 'restaurants'}>
      <InView
        triggerOnce
        onChange={async (inView) => {
          if (inView && activeTab === 'restaurants') {
            if (vendorsPerPage?.length > 0 && page === 1) {
              await metricSender.onLoadedRestaurants();
              trackingRestaurants({
                type: 'promotions_landing.loaded',
                payload: {
                  origin: origin || DEFAULT_ORIGIN_RESTAURANTS,
                  contentsIds: vendorsPerPage,
                  page,
                  quantitySize: pagination?.size,
                  quantityTotal: pagination?.total,
                  businessTypes: ['RESTAURANT'],
                  type: 'partner',
                  promotionsContent: 'SWIMLANE_ITEM_FOOD',
                  screenType: 'promotions-section-restaurants',
                },
              });
            }
          }
        }}
      >
        {!isRevamp && renderVendors(0, 2)}
        {!isRevamp && !loading ? <Space mt={32} /> : null}
        {!isRevamp && !loading ? <Space mt={16} /> : null}
        {!isRevamp && vendors.length > 2 && renderVendors(2, vendors.length)}
        {isRevamp && renderProductSwimlaneMVertical()}

        {renderSkeleton()}

        <InView
          onChange={(inView) => {
            if (inView && totalVendors - page * size > 0) {
              setPage(page + 1);
              setLoading(true);
            }
          }}
        >
          <div id="loader_more_vendors" />
        </InView>
      </InView>
    </Container>
  );
};
